import * as utils from '../../utils';
import pRetry from 'p-retry';
import { delay } from '../../utils';
import { getLogger } from '../../commons/logger';
import { PageNotAvailableError } from '../../errors';
import type { WebDriver } from '../webdriver';
import type { IWindowUtils } from 'clickim/src/background/session/ITabService';

const logger = getLogger('window-utils');
type Position = Record<'x' | 'y', number>;

export class WindowUtils implements IWindowUtils {
    /**
     * @param id tab or worker id
     */
    constructor(private readonly id: number, private readonly driver: WebDriver) {}

    // Seems unused and not working since bad args to executeJS
    async getElementFromPoint(_x: number, _y: number) {
        function elementFromPoint(x: number, y: number) {
            const el = document.elementFromPoint(x, y);
            return { testimId: el ? el.getAttribute('testim_dom_element_id') : null, tagName: el ? el.tagName : null };
        }

        const { value } = await this.driver.executeJS(elementFromPoint, _x, _y);
        return value;
    }

    getLocation() {
        return this.driver.getUrl();
    }

    async stopListeningToScroll() {
        // noop
    }

    async resumeListeningToScroll() {
        // noop
    }

    scrollToPosition(pos: Position) {
        return this.driver.scroll(pos.x, pos.y);
    }

    scrollToPositionWithoutAnimation(pos: Position) {
        // if scroll behavior is not supported, then the scrolling is not animated anyway
        function scrollWithoutAnimation(position: Position) {
            const scrollBehaviorSupported = 'scrollBehavior' in document.documentElement.style;
            if (scrollBehaviorSupported) {
                return window.scrollTo({ left: position.x, top: position.y, behavior: 'instant' });
            }
            return window.scrollTo(position.x, position.y);
        }
        return this.driver.executeJS(scrollWithoutAnimation, pos);
    }

    async getCurrentScrollPosition() {
        function scrollPosition() {
            return { x: window.scrollX, y: window.scrollY };
        }

        const { value } = await this.driver.executeJS(scrollPosition);
        return value;
    }

    navigate(location: string, navigationMaxTime = 15000) {
        const navigate = async (retries = 3): Promise<void> => {
            try {
                await this.driver.url(location);
            } catch (err) {
                const shouldRetryNavigation = err.message.includes('method IWebBrowser2::Navigate2() failed');
                // TODO: seleniumStack doesn't exist anymore. need to figure out how to fix this, or if we even need this anymore.
                if (err.seleniumStack && shouldRetryNavigation && retries > 0) {
                    logger.warn('selenium navigation failed. retrying to navigate', { err });
                    await delay(1500);
                    return navigate(retries - 1);
                }
                if (!err.seleniumStack && shouldRetryNavigation) {
                    logger.warn('selenium navigation failed. Due to wdio7 the error is unhandled', { err });
                }
                throw err;
            }
            return undefined;
        };

        return Promise.race([navigate(), delay(navigationMaxTime)]);
    }

    reloadTab(timeoutMSec = 15000) {
        return Promise.race([
            this.driver.reloadTab(),
            delay(timeoutMSec),
        ]);
    }

    async getZoom() {
        function zoom() {
            return { zoomLevel: window.outerWidth / window.innerWidth };
        }

        const { value } = await this.driver.executeJS(zoom);
        return value;
    }

    getViewportSize() {
        return this.driver.getViewportSize();
    }

    maximizeWithoutValidation() {
        return this.driver.maximizeWithoutValidation();
    }

    async getFullPageSize() {
        function fullPageSize() {
            const body = document.body;
            const html = document.documentElement;
            const height = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
            const width = Math.max(body.scrollWidth, body.offsetWidth, html.clientWidth, html.scrollWidth, html.offsetWidth);
            return {
                height,
                width,
            };
        }

        const { value } = await this.driver.executeJS(fullPageSize);
        return value;
    }

    async extractToNewWindow() {
        // noop
    }

    async checkSize(size: { width: number; height: number }) {
        await utils.delay(1000);
        const actualSize = await this.getViewportSize();
        if (actualSize.width !== size.width || actualSize.height !== size.height) {
            throw Object.assign(new Error('checkSize failed'), { actualSize, expectedSize: size });
        }
        return { actualSize, expectedSize: size };
    }

    async setViewportSize(size: { width: number; height: number }) {
        await this.driver.setViewportSize(size.width, size.height);
        return await this.checkSize(size);
    }

    async validatePageIsAvailable() {
        function pageIsAvailable() {
            let locationObj;
            if (typeof window !== 'undefined' && window.location !== undefined) {
                locationObj = window.location;
            } else {
                return false;
            }

            return (
                locationObj.href !== 'chrome-error://chromewebdata/' &&
                locationObj.href !== 'safari-resource:/ErrorPage.html' &&
                !locationObj.href.startsWith('res://ieframe.dll/http_404.htm') &&
                !locationObj.href.startsWith('ms-appx-web://microsoft.microsoftedge/assets/errorpages/')
            );
        }

        const result = await this.driver.executeJS(pageIsAvailable);
        if (!result.value) {
            throw new PageNotAvailableError('validatePageIsAvailable:PageNotAvailableError');
        }
    }

    focusTab() {
        return this.driver.switchTab(this.id as unknown as string);
    }

    quit() {
        return undefined;
    }

    async getOsAndBrowser() {
        const osAndBrowser = await pRetry(() => this.driver.getBrowserAndOS(), { retries: 3, factor: 1 });
        return {
            uaBrowserName: osAndBrowser.browser,
            uaOs: osAndBrowser.os,
            userAgent: osAndBrowser.userAgent,
            browserVersion: osAndBrowser.browserVersion,
        };
    }
}
